import { useEffect, useCallback, useState } from 'react';
import { useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { SOCKET_EVENTS } from '@maios/shared';
import { getSocket } from '../lib/socket';
import { useAuthStore } from '../store/authStore';
import { usePanelContext } from '../contexts/PanelContext';
import { useChatUI } from '../hooks/useChatUI';
import ChatMessageList from '../components/chat/ChatMessageList';
import ChatInput from '../components/chat/ChatInput';
import ThreadPanel from '../components/chat/ThreadPanel';
import PinnedMessageBanner from '../components/chat/PinnedMessageBanner';
import PinnedMessageList from '../components/chat/PinnedMessageList';
import type { ProjectDetail } from '../services/projectService';
import { getChannels, getChannel, getPinnedMessages } from '../services/chatChannelService';
import Spinner from '../components/ui/Spinner';

export default function ProjectChatPage() {
  const { project, clearChatUnread } = useOutletContext<{ project: ProjectDetail; clearChatUnread: () => void }>();
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id);

  const {
    messages,
    isLoading,
    loadingMore,
    hasMore,
    sending,
    sendMessage,
    loadMore,
    enterChat,
    leaveChat,
    showNewMsgBtn,
    handleNewMsgBtnClick,
    handleScrollChange,
    scrollContainerRef,
    messagesEndRef,
  } = useChatUI();

  // CHG-075: 프로젝트 채널 ID 조회 (파일 업로드용)
  const { data: channelsData } = useQuery({
    queryKey: ['chatChannels', 'project', project.id],
    queryFn: () => getChannels({ type: 'PROJECT' }),
    select: (d) => d.channels.find((ch) => ch.projectId === project.id),
  });
  const projectChannelId = channelsData?.id;

  // CHG-131: 채널 상세 (myRole — 액션 버튼 권한 분기에 필요)
  const { data: channelDetail } = useQuery({
    queryKey: ['chatChannel', projectChannelId],
    queryFn: () => getChannel(projectChannelId!),
    enabled: !!projectChannelId,
  });
  const currentUserRole = channelDetail?.myRole ?? null;

  // CHG-131: 스레드 진입점 (액션 버튼 "스레드에 답글"이 보이려면 onOpenThread 필요)
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const invalidatePins = useCallback(() => {
    if (projectChannelId) queryClient.invalidateQueries({ queryKey: ['pinnedMessages', projectChannelId] });
  }, [queryClient, projectChannelId]);

  // CHG-131: 고정 메시지 배너/목록
  const { data: pinnedData } = useQuery({
    queryKey: ['pinnedMessages', projectChannelId],
    queryFn: () => getPinnedMessages(projectChannelId!, 1, 1),
    enabled: !!projectChannelId,
  });
  const pinnedCount = pinnedData?.pagination?.total ?? 0;
  const [showPinnedList, setShowPinnedList] = useState(false);

  // REQ-V31-016: 채팅 페이지 진입 시 소켓 룸 참가 + 뱃지 해제
  useEffect(() => {
    enterChat();
    clearChatUnread();
    return () => leaveChat();
  }, [enterChat, leaveChat, clearChatUnread]);

  // CHG-131: 채널 소켓 룸 가입 (CHANNEL_THREAD_MESSAGE / CHANNEL_MESSAGE_PINNED 등 채널 이벤트 수신용)
  useEffect(() => {
    if (!projectChannelId) return;
    const socket = getSocket();
    if (!socket) return;
    socket.emit(SOCKET_EVENTS.CHANNEL_JOIN, { channelId: projectChannelId });
    return () => {
      socket.emit(SOCKET_EVENTS.CHANNEL_LEAVE, { channelId: projectChannelId });
    };
  }, [projectChannelId]);

  const { openDetailPanel } = usePanelContext();

  const handleMentionClick = (issueKey: string) => {
    navigate(`/projects/${project.projectKey}/issues?search=${issueKey}`);
  };

  // REQ-V35-006: MentionCard 클릭 → 작업 상세 패널 열기
  const handleMentionCardClick = useCallback(
    (issueId: string) => {
      openDetailPanel('issue', issueId);
    },
    [openDetailPanel],
  );

  // CHG-123 REQ-V123-042: ?messageId 도착 시 자동 스크롤 + 하이라이트
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightedMsgId, setHighlightedMsgId] = useState<string | null>(null);

  // CHG-131: 고정 메시지 점프 (PinnedMessageBanner/List에서 사용)
  const handleJumpToMessage = useCallback((messageId: string) => {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('messageId', messageId); return next; }, { replace: false });
  }, [setSearchParams]);
  useEffect(() => {
    const targetMsgId = searchParams.get('messageId');
    if (!targetMsgId) return undefined;
    const exists = messages.some((m) => m.messageId === targetMsgId);
    if (exists) {
      setHighlightedMsgId(targetMsgId);
      requestAnimationFrame(() => {
        const el = document.getElementById(`msg-${targetMsgId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      const timer = setTimeout(() => setHighlightedMsgId(null), 2500);
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('messageId'); return next; }, { replace: true });
      return () => clearTimeout(timer);
    }
    if (messages.length > 0 && !isLoading) {
      toast('원본 메시지를 찾을 수 없습니다. 이전 페이지에 있을 수 있습니다.');
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('messageId'); return next; }, { replace: true });
    }
    return undefined;
  }, [searchParams, messages, setSearchParams, isLoading]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <div className="flex flex-col flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden">
        {/* CHG-131: 고정 메시지 배너 + 목록 (LNB 채팅과 동일) */}
        {projectChannelId && (
          <div className="relative shrink-0">
            {pinnedCount > 0 && (
              <PinnedMessageBanner
                channelId={projectChannelId}
                onToggleList={() => setShowPinnedList((v) => !v)}
                onJumpToMessage={handleJumpToMessage}
              />
            )}
            <PinnedMessageList
              channelId={projectChannelId}
              isOpen={showPinnedList}
              onClose={() => setShowPinnedList(false)}
              onJumpToMessage={handleJumpToMessage}
              currentUserRole={currentUserRole}
              currentUserId={userId}
            />
          </div>
        )}
        <ChatMessageList
          messages={messages}
          isLoading={isLoading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          currentUserId={userId}
          onMentionClick={handleMentionClick}
          onMentionCardClick={handleMentionCardClick}
          showNewMsgBtn={showNewMsgBtn}
          onNewMsgBtnClick={handleNewMsgBtnClick}
          scrollContainerRef={scrollContainerRef as React.RefObject<HTMLDivElement>}
          messagesEndRef={messagesEndRef as React.RefObject<HTMLDivElement>}
          onScrollChange={handleScrollChange}
          channelId={projectChannelId}
          channelType="PROJECT"
          contextProjectId={project.id}
          contextProjectKey={project.projectKey}
          highlightedMessageId={highlightedMsgId}
          onOpenThread={setActiveThreadId}
          currentUserRole={currentUserRole}
          onPinChange={invalidatePins}
        />
        <ChatInput
          projectId={project.id}
          channelId={projectChannelId}
          // CHG-131: ChatInput → (content, attachments?, mentions?). ChatContext.sendMessage는 (content, mentions?)만 받음.
          onSend={(content, _attachments, mentions) => sendMessage(content, mentions)}
          sending={sending}
          channelMembers={channelDetail?.members?.map((m) => ({ userId: m.userId, name: m.name, avatarUrl: m.avatarUrl }))}
          currentUserRole={currentUserRole}
          currentUserId={userId}
        />
      </div>

      {/* CHG-131: 스레드 진입 시 우측 ThreadPanel — 메인 채팅과 동일한 rounded-xl 박스로 래핑 */}
      {activeThreadId && projectChannelId && (
        <div className="ml-3 flex flex-col flex-shrink-0 w-96 bg-white border border-gray-200 rounded-xl overflow-hidden">
          {/* 헤더 — 메인 채팅 헤더와 동일 높이/스타일 */}
          <div className="h-14 flex items-center justify-between px-4 border-b shrink-0">
            <h3 className="font-semibold text-base text-gray-900">스레드</h3>
            <button
              onClick={() => setActiveThreadId(null)}
              className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              title="닫기"
            >
              <X className="size-5" />
            </button>
          </div>
          <ThreadPanel
            channelId={projectChannelId}
            parentMessageId={activeThreadId}
            projectId={project.id}
            onClose={() => setActiveThreadId(null)}
            channelType="PROJECT"
            contextProjectKey={project.projectKey}
            embedded
          />
        </div>
      )}
    </div>
  );
}
